import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { sourceText } from "./draftText";
import { wordDiff } from "./wordDiff";
import type { WorkshopMeta } from "./WorkshopFrame";
import { errorText, workshopApi } from "./workshopApi";
import type { Draft, Version } from "./types";

const SUGGESTIONS = [
  "Kürze den Sachverhalt um ein Drittel.",
  "Formuliere das Petitum überprüfbarer.",
  "Schreib sachlicher, ohne wertende Adjektive.",
  "Mach den Betreff kürzer.",
];

const usd = (v: number) =>
  v.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const time = (t: string) =>
  new Date(t).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
const countWords = (s: string) => s.match(/\S+/g)?.length ?? 0;

// Left column of a finished draft: the revision history as a small chat
// (instruction as user bubble, version as answer), input at the bottom.
export const RevisionPanel = ({
  draft,
  locked,
  unsaved,
  selectedNr,
  meta: { status, setStatus },
  onUpdated,
  onCompare,
  onRestore,
}: {
  draft: Draft;
  locked: boolean;
  /** Unsaved hand edits: revising and restoring would silently drop them. */
  unsaved: boolean;
  selectedNr: number | null;
  meta: WorkshopMeta;
  onUpdated: (d: Draft) => void;
  onCompare: (v: Version | null) => void;
  onRestore: (nr: number) => Promise<void>;
}) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [texts, setTexts] = useState<Record<number, string>>({});
  const [prompt, setPrompt] = useState("");
  const [running, setRunning] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const current = draft.aktuelle_nr;

  useEffect(() => {
    setTexts({});
  }, [draft.id]);

  useEffect(() => {
    workshopApi
      .versions(draft.id)
      .then(setVersions)
      .catch(() => setVersions([]));
  }, [draft.id, current]);

  // Version texts never change, so each one is fetched once for the change hints.
  useEffect(() => {
    const missing = versions.filter((v) => texts[v.nr] === undefined);
    if (!missing.length) return;
    Promise.all(
      missing.map((v) => workshopApi.version(draft.id, v.nr).catch(() => null)),
    ).then((rs) =>
      setTexts((t) => {
        const next = { ...t };
        for (const r of rs) if (r?.text !== undefined) next[r.nr] = r.text;
        return next;
      }),
    );
  }, [versions, draft.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changes = useMemo(() => {
    const m: Record<number, { plus: number; minus: number; base: number }> = {};
    for (const v of versions) {
      // A restore's parent is the restored version itself; compare it with the
      // version current before it, always nr - 1 (versions append at max + 1).
      const base = v.quelle === "wiederherstellen" ? v.nr - 1 : v.eltern_nr;
      const before = base !== null ? texts[base] : undefined;
      const after = texts[v.nr];
      if (before === undefined || after === undefined || v.quelle === "lauf")
        continue;
      let plus = 0;
      let minus = 0;
      for (const part of wordDiff(before, after)) {
        if (part.kind === "added") plus += countWords(part.text);
        else if (part.kind === "removed") minus += countWords(part.text);
      }
      m[v.nr] = { plus, minus, base: base! };
    }
    return m;
  }, [versions, texts]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [versions.length, running, error]);

  const max = status?.max_anweisung ?? 500;
  const budget = status?.budget_usd ?? null;
  const spent = status?.verbraucht_usd ?? 0;
  const share = budget ? spent / budget : 0;
  const exhausted = budget !== null && share >= 1;
  const now = new Date();
  const month = now.toLocaleDateString("de-DE", { month: "long" });
  const nextMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    1,
  ).toLocaleDateString("de-DE", { month: "long" });

  const send = async () => {
    const text = prompt.trim();
    if (!text || !current || locked || unsaved) return;
    setRunning(text);
    setError(null);
    try {
      const r = await workshopApi.iterate(draft.id, text, current);
      onUpdated(r.entwurf);
      setPrompt("");
      setStatus((s) => (s ? { ...s, verbraucht_usd: r.verbraucht_usd } : s));
    } catch (e) {
      setError(errorText(e));
      // A 409 means someone else saved meanwhile: show their version.
      workshopApi
        .draft(draft.id)
        .then(onUpdated)
        .catch(() => {});
      workshopApi
        .status()
        .then((s) => setStatus(() => s))
        .catch(() => {});
    } finally {
      setRunning(null);
    }
  };

  const restore = async (nr: number) => {
    setRestoring(nr);
    try {
      await onRestore(nr);
    } finally {
      setRestoring(null);
    }
  };

  const answer = (v: Version) => {
    const d = changes[v.nr];
    const what =
      v.quelle === "lauf"
        ? "Erster Entwurf aus der Recherche"
        : v.quelle === "hand"
          ? `Von Hand bearbeitet${v.ersteller ? ` von ${v.ersteller}` : ""}`
          : v.quelle === "wiederherstellen"
            ? `Fassung ${v.eltern_nr} zurückgeholt`
            : "Überarbeitet";
    const isCurrent = v.nr === current;
    const isSelected = v.nr === selectedNr;
    return (
      <div
        className={cn(
          "rounded-2xl rounded-tl-sm border px-3 py-2 text-sm",
          isSelected ? "border-primary bg-primary-soft" : "bg-card",
        )}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-medium">Fassung {v.nr}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {time(v.erstellt_am)}
          </span>
        </div>
        <p className="text-muted-foreground">
          {what}
          {d && (d.plus || d.minus) ? (
            <span className="tabular-nums">
              {" · "}
              <span className="text-success">+{d.plus}</span>{" "}
              <span className="text-destructive">−{d.minus}</span> Wörter
              {v.quelle === "wiederherstellen" &&
                ` gegenüber Fassung ${d.base}`}
            </span>
          ) : d ? (
            v.quelle === "wiederherstellen" ? (
              ` · Text gleich wie Fassung ${d.base}`
            ) : (
              " · Text unverändert"
            )
          ) : null}
        </p>
        {v.hinweise?.map((h) => (
          <p
            key={h}
            className="mt-1 rounded-md bg-warning-soft px-2 py-1 text-xs text-warning-text"
          >
            {h}
          </p>
        ))}
        {v.quellen && v.quellen.length > 0 && (
          <ul className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
            {v.quellen.map((q) => (
              <li key={`${q.art}:${q.nummer ?? q.url}`} className="truncate">
                <span>{q.art === "web" ? "Web: " : "BV: "}</span>
                {q.url ? (
                  <a
                    href={q.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    {sourceText(q)}
                  </a>
                ) : (
                  sourceText(q)
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-1.5 flex gap-3 text-xs">
          {isCurrent ? (
            <span className="font-medium text-primary">aktuell</span>
          ) : (
            <>
              <button
                type="button"
                onClick={() =>
                  onCompare(
                    isSelected ? null : { ...v, text: texts[v.nr] ?? v.text },
                  )
                }
                className="text-primary hover:underline"
              >
                {isSelected ? "Vergleich schließen" : "Vergleichen"}
              </button>
              <button
                type="button"
                onClick={() => restore(v.nr)}
                disabled={unsaved || restoring !== null || running !== null}
                className="text-primary hover:underline disabled:opacity-50"
              >
                {restoring === v.nr ? "Wird zurückgeholt …" : "Zurückholen"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col rounded-xl border bg-card lg:h-[calc(100vh-7rem)]">
      <div className="border-b px-4 pt-4 pb-3">
        <h3 className="text-sm font-semibold">Überarbeiten</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Sag, was sich ändern soll. Jede Überarbeitung wird eine neue Fassung,
          keine geht verloren.
        </p>
      </div>

      <ol
        ref={listRef}
        className="flex max-h-[28rem] flex-1 flex-col gap-3 overflow-y-auto bg-muted/40 px-4 py-4 lg:max-h-none"
      >
        {versions.map((v) => (
          <li key={v.nr} className="flex flex-col gap-1.5">
            {v.quelle === "prompt" && v.prompt && (
              <div className="flex flex-col items-end">
                {v.ersteller && (
                  <span className="mb-0.5 text-xs text-muted-foreground">
                    {v.ersteller}
                  </span>
                )}
                <p className="max-w-[90%] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
                  {v.prompt}
                </p>
              </div>
            )}
            <div className="max-w-[92%]">{answer(v)}</div>
          </li>
        ))}
        {running && (
          <li className="flex flex-col gap-1.5">
            <div className="flex justify-end">
              <p className="max-w-[90%] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
                {running}
              </p>
            </div>
            <div className="flex max-w-[92%] items-center gap-2 rounded-2xl rounded-tl-sm border bg-card px-3 py-2 text-sm text-muted-foreground">
              <span className="size-2 animate-pulse rounded-full bg-primary" />
              Wird überarbeitet … mit Recherche bis zu einer Minute
            </div>
          </li>
        )}
        {error && !running && (
          <li
            role="alert"
            className="max-w-[92%] whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-destructive-soft px-3 py-2 text-sm text-destructive"
          >
            {error}
          </li>
        )}
      </ol>

      <div className="flex flex-col gap-2 border-t p-3">
        {exhausted ? (
          <p className="rounded-md bg-warning-soft px-3 py-2 text-sm text-warning-text">
            Dein Budget für {month} ({usd(budget!)} USD) ist aufgebraucht. Ab 1.{" "}
            {nextMonth} kannst du wieder überarbeiten. Bearbeiten von Hand und
            Zurückholen gehen weiter.
          </p>
        ) : locked || unsaved ? (
          <p className="text-xs text-muted-foreground">
            {locked
              ? "Während du von Hand bearbeitest, ruht das Überarbeiten."
              : "Erst speichern oder verwerfen, dann geht das Überarbeiten weiter."}
          </p>
        ) : (
          <>
            {!prompt && !running && (
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setPrompt(s)}
                    className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <Textarea
              aria-label="Anweisung zum Überarbeiten"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value.slice(0, max))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
              }}
              rows={3}
              disabled={running !== null}
              placeholder="z. B. Mach aus Ziffer 2 zwei getrennte Forderungen."
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs tabular-nums text-muted-foreground">
                {prompt.length} / {max}
                {budget !== null && ` · ${usd(spent)} von ${usd(budget)} USD`}
              </span>
              <Button
                size="sm"
                onClick={send}
                disabled={running !== null || !prompt.trim() || !current}
              >
                {running ? "Läuft …" : "Senden"}
              </Button>
            </div>
            {budget !== null && share >= 0.8 && (
              <p className="text-xs text-warning-text">
                Du hast {Math.floor(share * 100)} % deines Budgets für {month}{" "}
                verbraucht.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
